import {
  AdminCustomProviderSelect,
  AdminCustomVDriveProviderSelect,
  AdminDatePickerInputModal,
  AdminInput,
  Button,
  DeliveryAssignmentProviderEntity,
  Div,
  ErrorToast,
  format24hToDate,
  formatDateToTime24h,
  handleChangeNumInputTwoDecimalsMinMax,
  ModalPortal,
  P,
  Provider,
  VDriveProvider,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

export const AddDeliveryProvider = ({
  isOpen,
  setIsOpen,
  setProviders,
  availableProviders,
  bannedProviders = [],
  edit,
}: {
  isOpen: boolean | { item: DeliveryAssignmentProviderEntity; index: number };
  setIsOpen: Dispatch<
    SetStateAction<
      boolean | { item: DeliveryAssignmentProviderEntity; index: number }
    >
  >;
  setProviders: Dispatch<SetStateAction<DeliveryAssignmentProviderEntity[]>>;
  availableProviders: VDriveProvider[];
  bannedProviders?: VDriveProvider[];
  edit?: boolean;
}) => {
  const [provider, setProvider] = useState<string | VDriveProvider | null>('');

  const [orderProviders, setOrderProviders] = useState<Provider[] | null>([]);
  const [fallbackProviders, setFallbackProviders] = useState<
    VDriveProvider[] | null
  >([]);
  const [startTime, setStartTime] = useState<Date[]>([]);
  const [endTime, setEndTime] = useState<Date[]>([]);
  const [distanceMiles, setDistanceMiles] = useState('');

  function toggleModal() {
    setIsOpen((prev) => !prev);
    clearState();
  }

  useEffect(() => {
    if (edit && isOpen && typeof isOpen === 'object') {
      setProvider(isOpen.item.provider);
      setOrderProviders(isOpen.item.orderProviders || []);
      setStartTime(
        isOpen.item.startTime ? [format24hToDate(isOpen.item.startTime)] : []
      );
      setEndTime(
        isOpen.item.endTime ? [format24hToDate(isOpen.item.endTime)] : []
      );
      setDistanceMiles(isOpen.item.distanceMiles?.toString() || '');
      setFallbackProviders(isOpen.item.fallbackProviders || []);
    }
  }, [isOpen]);

  const clearState = () => {
    setProvider('');
    setOrderProviders([]);
    setFallbackProviders([]);
    setStartTime([]);
    setEndTime([]);
    setDistanceMiles('');
  };

  const handleSubmitAdd = () => {
    if (!provider) {
      return ErrorToast('Please Select Provider.');
    }

    const hasStartOrEnd = startTime[0] || endTime[0];
    const hasDistance = !!distanceMiles;
    const hasOrderProvider = orderProviders && orderProviders?.length > 0;

    if (!hasStartOrEnd && !hasDistance && !hasOrderProvider) {
      return ErrorToast(
        'Please provide at least one of: Distance, Time range or Order Provider.'
      );
    }

    if (hasStartOrEnd) {
      if ((startTime && !endTime) || (!startTime && endTime)) {
        return ErrorToast('Start Time and End Time must be provided together.');
      }
      if (startTime && endTime && startTime[0] >= endTime[0])
        return ErrorToast('Time From must be earlier than Time To');
    }

    const objectToAdd = {
      provider,
      startTime: startTime[0] ? formatDateToTime24h(startTime[0]) : null,
      endTime: endTime[0] ? formatDateToTime24h(endTime[0]) : null,
      distanceMiles: Number(distanceMiles) || null,
      orderProviders: orderProviders || null,
      fallbackProviders: [],
    };

    setProviders((prev) => [
      ...prev,
      objectToAdd as DeliveryAssignmentProviderEntity,
    ]);
    toggleModal();
  };

  const handleSubmitEdit = () => {
    if (!provider) {
      return ErrorToast('Please Select Provider.');
    }

    const hasStartOrEnd = startTime || endTime;
    const hasDistance = !!distanceMiles;
    const hasOrderProvider = !!orderProviders;

    if (!hasStartOrEnd && !hasDistance && !hasOrderProvider) {
      return ErrorToast(
        'Please provide at least one of: Distance, Time range or Order Provider.'
      );
    }

    if (hasStartOrEnd) {
      if ((startTime && !endTime) || (!startTime && endTime)) {
        return ErrorToast('Start Time and End Time must be provided together.');
      }
      if (startTime && endTime && startTime[0] >= endTime[0])
        return ErrorToast('Time From must be earlier than Time To');
    }

    const objectToEdit = {
      provider,
      orderProviders: orderProviders || null,
      startTime: startTime[0] ? formatDateToTime24h(startTime[0]) : null,
      endTime: endTime[0] ? formatDateToTime24h(endTime[0]) : null,
      distanceMiles: Number(distanceMiles) || null,
      fallbackProviders: fallbackProviders || null,
    };

    setProviders((prev) =>
      prev.map((p, i) =>
        i ===
        (isOpen as { item: DeliveryAssignmentProviderEntity; index: number })
          .index
          ? ({ ...p, ...objectToEdit } as DeliveryAssignmentProviderEntity)
          : (p as DeliveryAssignmentProviderEntity)
      )
    );
    toggleModal();
  };

  const existsInFallbackProviders = (p: VDriveProvider) =>
    fallbackProviders?.includes(p);

  const removeFromFallbackProviders = (provider: VDriveProvider) => {
    setFallbackProviders((prev) => prev?.filter((p) => p !== provider) || []);
  };

  return (
    <ModalPortal
      header={edit ? 'Edit Delivery Provider' : 'Add Delivery Provider'}
      isOpen={!!isOpen}
      closeModal={toggleModal}
      overflow="visible"
      zIndex={131}
    >
      <Div $flex $column $gap="24px">
        <AdminCustomVDriveProviderSelect
          value={provider}
          onChange={(value) => {
            if (existsInFallbackProviders(value as VDriveProvider)) {
              removeFromFallbackProviders(value as VDriveProvider);
            }
            setProvider(value as VDriveProvider);
          }}
          label="Delivery Provider"
          placeholder="Choose Fleet"
          $shadowDesign={false}
          $textCenter
          providerArray={availableProviders}
          disabledProviders={bannedProviders}
        />
        <AdminInput
          label="Distance in Miles"
          $textCenter
          value={distanceMiles}
          placeholder="Enter here"
          onChange={(e) =>
            handleChangeNumInputTwoDecimalsMinMax(
              e,
              setDistanceMiles,
              0,
              1000000
            )
          }
        />
        <div>
          <P $bold $mb="8" $center>
            Select Hours From - To
          </P>
          <Div $flex $gap="24px">
            <AdminDatePickerInputModal
              format="hh:mm a"
              options={{
                enableTime: true,
                dateFormat: 'Y-m-d H:i',
                noCalendar: true,
              }}
              placeholder="From"
              $textCenter
              onChange={setStartTime}
              value={startTime}
            />
            <AdminDatePickerInputModal
              format="hh:mm a"
              options={{
                enableTime: true,
                dateFormat: 'Y-m-d H:i',
                noCalendar: true,
              }}
              placeholder="To"
              $textCenter
              onChange={setEndTime}
              value={endTime}
            />
          </Div>
        </div>
        <AdminCustomProviderSelect
          value={orderProviders}
          onChange={(value) => {
            setOrderProviders(value as Provider[]);
          }}
          label="Order Providers"
          placeholder="Select Providers"
          $textCenter
          $shadowDesign={false}
          multiSelect
        />

        <AdminCustomVDriveProviderSelect
          value={fallbackProviders}
          onChange={(value) => {
            setFallbackProviders(value as VDriveProvider[]);
          }}
          label="Fallback Providers"
          placeholder="Select Providers"
          $textCenter
          $shadowDesign={false}
          multiSelect
          providerArray={availableProviders}
          disabledProviders={[...bannedProviders, provider]}
        />
      </Div>
      <Button
        $w="100%"
        $mt="32"
        $selected
        onClick={edit ? handleSubmitEdit : handleSubmitAdd}
      >
        Confirm
      </Button>
      <Button $w="100%" $mt="16" $selected $gray onClick={toggleModal}>
        Cancel
      </Button>
    </ModalPortal>
  );
};
